import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { buildClasses } from "../utils/buildClasses";
import { RyeNoResults } from "../rye/RyeNoResults";
import { RyeMainContentHeader } from "../rye/RyeMainContentHeader";
import { RyeMainContentSpacer } from "../rye/RyeMainContentSpacer";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { RyeMainContentSplitPane } from "../rye/RyeMainContentSplitPane";
import { RyeSegmentedButton } from "../rye/RyeSegmentedButton";
import { TransactionsDrilldownPane } from "../common/TransactionsDrilldownPane";
import type { TopMerchantsReportState } from "./cashFlowState";
import { ReportDateRangeControl } from "./ReportDateRangeControl";
import { resolveDateRange } from "./reportDateRange";
import { BreakdownSelection, TYPE_LABELS } from "./breakdownChartParts";
import { TopMerchantsBars } from "./TopMerchantsBars";
import {
  MERCHANT_TYPES,
  MerchantType,
  useTopMerchantsData,
} from "./useTopMerchantsData";

export function TopMerchantsReport({
  state,
  setState,
}: {
  state: TopMerchantsReportState;
  setState: (
    update: (prev: TopMerchantsReportState) => TopMerchantsReportState,
  ) => void;
}): JSX.Element {
  const { dateRange, type, sort } = state;

  // Memoized so they keep a stable identity across renders — the data hook
  // keys its work off of them.
  const { beginDate, endDate } = useMemo(
    () => resolveDateRange(dateRange, dayjs()),
    [dateRange],
  );

  const data = useTopMerchantsData(beginDate, endDate, type, sort);
  const questionKey = [
    type,
    sort,
    beginDate.toISOString(),
    endDate.toISOString(),
  ].join("|");

  const [selection, setSelection] = useState<BreakdownSelection | null>(null);

  useEffect(() => {
    setSelection(null);
  }, [questionKey]);

  return (
    <>
      <RyeMainContentHeader title="Top merchants" roundedTl={false}>
        <div
          className={buildClasses("flex", "items-center", "gap-3", "w-full")}
        >
          <ReportDateRangeControl
            presets={[
              "this-month",
              "last-month",
              "year-to-date",
              "last-12-months",
            ]}
            range={dateRange}
            onChange={(update) =>
              setState((prev) => ({
                ...prev,
                dateRange: update(prev.dateRange),
              }))
            }
          />
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "gap-3",
              "ml-auto",
              "shrink-0",
            )}
          >
            <RyeSegmentedButton
              entries={MERCHANT_TYPES.map((entry) => ({
                id: entry,
                displayText: TYPE_LABELS[entry],
                onClick: () => setState((prev) => ({ ...prev, type: entry })),
              }))}
              initEntryId={type}
            />
            <RyeSegmentedButton
              entries={[
                {
                  id: "total",
                  displayText: "Total",
                  tooltip: "Rank by total",
                  onClick: () =>
                    setState((prev) => ({ ...prev, sort: "total" })),
                },
                {
                  id: "frequency",
                  displayText: "Frequency",
                  tooltip: "Rank by number of transactions",
                  onClick: () =>
                    setState((prev) => ({ ...prev, sort: "frequency" })),
                },
              ]}
              initEntryId={sort}
            />
          </div>
        </div>
      </RyeMainContentHeader>
      <RyeMainContentSpacer direction="h" />
      <RyeMainContentSplitPane
        bottomPaneLabel="transactions pane"
        bottomPane={
          selection ? (
            <TransactionsDrilldownPane
              onClose={() => setSelection(null)}
              key={selection.id}
              groupLabel={selection.label}
              transactions={selection.transactions}
            />
          ) : null
        }
      >
        <RyeMainContentCard
          roundedTl={false}
          roundedTr={false}
          roundedBl={false} // meets the left nav
          roundedBr={selection === null}
        >
          {data.merchants.length === 0 ? (
            <EmptyState type={type} />
          ) : (
            <TopMerchantsBars
              data={data}
              type={type}
              sort={sort}
              questionKey={questionKey}
              selectedId={selection?.id ?? null}
              onSelect={setSelection}
            />
          )}
        </RyeMainContentCard>
      </RyeMainContentSplitPane>
    </>
  );
}

function EmptyState({ type }: { type: MerchantType }): JSX.Element {
  return (
    <RyeNoResults
      icon="storefront"
      title="Nothing to show for these dates"
      description={`There are no reviewed ${TYPE_LABELS[type].toLowerCase()} transactions with a merchant for this date range.`}
    />
  );
}
